import { cn } from '@/lib/utils';
import { useMemo, forwardRef, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ForwardedRef } from 'react';
import { motion } from 'motion/react';
import { toFullS3Url } from '@/utils/toFullS3Url';
import { WinnerBadge } from './drawing/WinnerBadge';
import { raffleTicketIsFlipped } from './drawing/ticketMotion';

export type RaffleTicketProps = {
  signatureImageUrl?: string | null;
  className?: string;
  visibleSide?: 'front' | 'back';
  isWinner?: boolean;
  delayedFlip?: number;
  selected?: boolean;
  dimmed?: boolean;
  stillIn?: boolean;
  showWinnerBadge?: boolean;
  winnerTilt?: number;
  compactWinner?: boolean;
  winnerBadgePlacement?: 'above' | 'on-ticket';
};

function RaffleTicketBase(
  {
    signatureImageUrl,
    className,
    visibleSide = 'back',
    isWinner = false,
    delayedFlip,
    selected = false,
    dimmed = false,
    stillIn = false,
    showWinnerBadge = false,
    winnerTilt = 0,
    compactWinner = false,
    winnerBadgePlacement = 'above',
  }: RaffleTicketProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { frontTicketStyle, backTicketStyle, signatureSrc } = useMemo(() => {
    const baseTicketStyle: CSSProperties = {
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      aspectRatio: '2',
    };
    return {
      frontTicketStyle: {
        ...baseTicketStyle,
        backgroundImage: `url(/raffle-ticket-front.svg)`,
      },
      backTicketStyle: {
        ...baseTicketStyle,
        backgroundImage: `url(/raffle-ticket-back.svg)`,
      },
      signatureSrc: signatureImageUrl ? toFullS3Url(signatureImageUrl) : null,
    };
  }, [signatureImageUrl]);

  const [isFlipped, setIsFlipped] = useState(() =>
    raffleTicketIsFlipped({
      visibleSide,
      delayedFlipElapsed: false,
    }),
  );
  const didFlipRef = useRef(false);

  useEffect(() => {
    if (delayedFlip) {
      if (didFlipRef.current) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        didFlipRef.current = true;
        setIsFlipped(
          raffleTicketIsFlipped({
            visibleSide,
            delayedFlipElapsed: true,
          }),
        );
      }, delayedFlip);
      return () => window.clearTimeout(timeoutId);
    }

    didFlipRef.current = false;
    setIsFlipped(
      raffleTicketIsFlipped({
        visibleSide,
        delayedFlipElapsed: false,
      }),
    );
  }, [delayedFlip, visibleSide]);

  return (
    <div
      ref={ref}
      data-name="RAFFLE_TICKET"
      data-selected={selected ? 'true' : 'false'}
      data-dimmed={dimmed ? 'true' : 'false'}
      data-still-in={stillIn ? 'true' : 'false'}
      data-winner={isWinner ? 'true' : 'false'}
      data-visible-side={visibleSide}
      data-flipped={isFlipped ? 'true' : 'false'}
      data-delayed-flip={delayedFlip ?? ''}
      className={cn(
        'relative flex flex-col items-center justify-center',
        className,
      )}
      style={{ aspectRatio: '2' }}
    >
      <motion.div
        className={cn(
          'absolute inset-0',
          selected && 'rounded-md ring-[3px] ring-[#FF6B3D]',
          stillIn && 'rounded-md ring-2 ring-[#50DC78]',
          compactWinner &&
            isWinner &&
            'rounded-md shadow-[0_0_24px_rgba(255,107,61,0.45)] ring-[3px] ring-[#FF6B3D]',
        )}
        animate={
          stillIn
            ? {
                boxShadow: [
                  '0 0 0px rgba(80,220,120,0)',
                  '0 0 28px rgba(80,220,120,0.55)',
                  '0 0 0px rgba(80,220,120,0.15)',
                ],
              }
            : selected
              ? {
                  scale: [1, 1.04, 1],
                  boxShadow: [
                    '0 0 0px rgba(255,107,61,0)',
                    '0 0 24px rgba(255,107,61,0.5)',
                    '0 0 18px rgba(255,107,61,0.35)',
                  ],
                }
              : isWinner
                ? compactWinner
                  ? {
                      scale: 1,
                      rotate: 0,
                      boxShadow: '0 0 24px rgba(255,107,61,0.5)',
                    }
                  : {
                      scale: 1.08,
                      rotate: winnerTilt,
                      boxShadow: '0 0 32px rgba(255,107,61,0.55)',
                    }
                : { scale: 1, rotate: 0, boxShadow: '0 0 0 rgba(0,0,0,0)' }
        }
        transition={
          stillIn
            ? { duration: 1.2, ease: 'easeInOut', repeat: Infinity }
            : selected
              ? { duration: 0.32, ease: 'easeInOut' }
              : isWinner
                ? { duration: 0.85, ease: 'easeOut' }
                : { duration: 0.2 }
        }
        style={{
          opacity: dimmed ? 0.35 : 1,
          filter: dimmed ? 'grayscale(0.25) brightness(0.9)' : undefined,
          transformStyle: 'preserve-3d',
        }}
      >
        {showWinnerBadge && (
          <div
            className={
              winnerBadgePlacement === 'on-ticket'
                ? 'absolute left-1 top-1 z-30 origin-top-left scale-75'
                : 'absolute -top-5 left-1/2 z-30 -translate-x-1/2'
            }
          >
            <WinnerBadge />
          </div>
        )}
        <div
          className="perspective-1000 absolute inset-0"
          style={{ perspective: '1000px', transformStyle: 'preserve-3d' }}
        >
          <div
            className="relative h-full w-full transition-transform duration-700 ease-in-out"
            style={{
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            <div
              className="backface-hidden absolute inset-0"
              style={{ ...frontTicketStyle, backfaceVisibility: 'hidden' }}
            />
            <div
              className="backface-hidden absolute inset-0"
              style={{
                ...backTicketStyle,
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              {signatureSrc ? (
                <img
                  src={signatureSrc}
                  alt=""
                  className="pointer-events-none absolute inset-0 h-full w-full object-fill"
                />
              ) : null}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export const RaffleTicket = forwardRef(RaffleTicketBase);
